import { CloudServerOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { useEnvironment } from './EnvironmentContext';
import type { HealthStatus } from './types';

const useStyles = createStyles(({ token }) => ({
  envFooter: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
    padding: '8px 12px',
    gap: '8px',
    cursor: 'pointer',
    color: 'inherit',
    borderRadius: token.borderRadius,
    transition: 'background-color 0.3s',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: token.colorBgTextHover,
    },
    ['&a']: {
      color: 'inherit',
    },
  },
  envFooterCollapsed: {
    justifyContent: 'center',
    padding: '8px 0',
  },
  dot: {
    flexShrink: 0,
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    border: '1px solid rgba(0,0,0,0.15)',
    display: 'inline-block',
    ['&[data-status="online"]']: {
      backgroundColor: '#52c41a',
      boxShadow: '0 0 6px rgba(82, 196, 26, 0.6)',
    },
    ['&[data-status="offline"]']: {
      backgroundColor: '#ff4d4f',
      boxShadow: '0 0 6px rgba(255, 77, 79, 0.6)',
    },
    ['&[data-status="checking"]']: {
      backgroundColor: '#faad14',
      animation: 'er-console-env-blink 1s ease-in-out infinite',
    },
    ['&[data-status="unknown"]']: {
      backgroundColor: '#8c8c8c',
    },
  },
  icon: {
    flexShrink: 0,
    fontSize: '15px',
  },
  name: {
    flexShrink: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '13px',
    lineHeight: 1,
  },
}));

const HEALTH_LABEL: Record<HealthStatus, string> = {
  online: '在线',
  offline: '离线',
  checking: '检测中',
  unknown: '未检测',
};

export interface EnvironmentStatusProps {
  collapsed?: boolean;
}

const EnvironmentStatus: React.FC<EnvironmentStatusProps> = ({ collapsed = false }) => {
  const { styles, cx } = useStyles();
  const { activeEnvironment, healthMap, openPanel } = useEnvironment();

  const health = activeEnvironment ? healthMap[activeEnvironment.id] : undefined;
  const status: HealthStatus = health?.status ?? 'unknown';

  const label = activeEnvironment
    ? `${activeEnvironment.name}（${HEALTH_LABEL[status]}）`
    : '未配置环境';
  const detail = activeEnvironment
    ? `${activeEnvironment.baseUrl}${health?.latencyMs != null ? ` · ${health.latencyMs}ms` : ''}${
        status === 'offline' && health?.detail ? ` · ${health.detail}` : ''
      }`
    : '点击配置环境';

  const content = (
    <div className={cx(styles.envFooter, collapsed && styles.envFooterCollapsed)}>
      <span className={styles.dot} data-status={status} />
      <CloudServerOutlined className={styles.icon} />
      {!collapsed && <span className={styles.name}>{activeEnvironment?.name ?? '未配置环境'}</span>}
    </div>
  );

  return (
    <Tooltip title={collapsed ? `${label}\n${detail}` : detail} placement="right">
      <div onClick={openPanel} role="button" tabIndex={0} aria-label={label}>
        {content}
      </div>
    </Tooltip>
  );
};

export default EnvironmentStatus;